import React, { useEffect, useState } from "react";
import { Clock, BookOpen, Timer, Award, FileText } from "lucide-react";
import axios from "../../routers/axios";
import { tokenManager } from "../../routers/api";
import { useNavigate, useLocation } from "react-router-dom";

const InstructionsScreen = ({
  onStartTest,
  testId,
  testName,
  scenarios,
  totalDuration = 0,
  resetTimer,
  testAttemptData,

}) => {
  const navigate = useNavigate();
  const [scenarioList, setScenarioList] = useState([]);
  const location = useLocation(); // thêm dòng này
  const viewMode = location.state?.viewMode || false;


  // ✅ Lấy thêm thông tin Reading/Writing
  useEffect(() => {
    const fetchScenarioDetails = async () => {
      if (!scenarios?.length) return;
      try {
        const detailedScenarios = await Promise.all(
          scenarios.map(async (s) => {
            const [reading, writing] = await Promise.all([
              axios.get(`/scenarios/reading/${s.id}`).then((r) => r.data).catch(() => null),
              axios.get(`/scenarios/writing/${s.id}`).then((r) => r.data).catch(() => null),
            ]);
            return { ...s, reading, writing };
          })
        );
        setScenarioList(detailedScenarios);
      } catch (err) {
        console.error("Error fetching Reading/Writing details:", err);
        setScenarioList(scenarios);
      }
    };
    fetchScenarioDetails();
  }, [scenarios]);

  // ✅ Start Test
  const handleStartTest = async (scenarioId = null) => {
    try {
      const user = tokenManager.getUser();
      if (!user || !testId) return;

      // 🧹 Xóa cache cũ nếu test chưa bắt đầu hoặc reset lại
      if (!testAttemptData || testAttemptData.status === "not_started") {
        localStorage.removeItem(`activeScenarioId_${testId}`);
      }

      const storedScenario = localStorage.getItem(`activeScenarioId_${testId}`);
      const finalScenarioId = scenarioId || Number(storedScenario) || scenarios[0]?.id;

      console.log("▶️ [Start Test] Using scenarioId =", finalScenarioId);

      await axios.post(`/test_attempts/start/${user.id}/${testId}/${finalScenarioId}`);

      // ✅ Xác định xem user đang dở phần Reading hay Writing
      const attemptRes = await axios.get(`/test_attempts/${user.id}/${testId}`);
      const scenarioAttempt = attemptRes.data?.attempts?.find(a => a.scenario_id === finalScenarioId);

      let nextStep = "reading";
      if (scenarioAttempt?.reading_submitted_at && !scenarioAttempt?.writing_submitted_at) {
        nextStep = "writing"; // Đã xong Reading → sang Writing
      }

      console.log("▶️ [Start Test] Resume step =", nextStep, "for scenario", finalScenarioId);
      const selectedScenario = scenarioList.find(s => s.id === finalScenarioId);
      onStartTest(nextStep, finalScenarioId, selectedScenario, viewMode);


    } catch (err) {
      console.error("Start test error:", err);
    }
  };


  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white shadow-md border-b flex-shrink-0">
        <div className="max-w-6xl mx-auto px-6 py-3 text-center">
          <div className="flex items-center justify-center">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center mr-3 shadow-md">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {testName || `Test ${testId}`}
            </h1>
          </div>
          <p className="text-base text-gray-600 mt-1">
            English Test System Instructions
          </p>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 max-w-6xl mx-auto w-full px-6 py-6 overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center justify-center">
          <FileText className="w-5 h-5 mr-2 text-blue-600" />
          Test Structure
        </h2>

        {/* ✅ Scenarios section */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {scenarioList?.map((s, idx) => (
            <div
              key={s.id}
              className={`rounded-2xl shadow-lg hover:scale-[1.01] transition-all duration-300 ${idx % 2 === 0
                ? "bg-blue-50 border-t-4 border-blue-400"
                : "bg-green-50 border-t-4 border-green-400"
                }`}
            >
              {/* Header */}
              <div className="px-5 py-3 border-b border-white/30 flex justify-between items-center">
                <h3
                  className={`font-semibold text-lg ${idx % 2 === 0 ? "text-blue-700" : "text-green-700"
                    }`}
                >
                  {idx % 2 === 0 ? "📘" : "📗"} Scenario {idx + 1}:{" "}
                  <span
                    className="ml-1"
                    dangerouslySetInnerHTML={{ __html: s.scenario_name }}
                  />
                </h3>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                {/* Reading */}
                {s.reading && (
                  <div className="flex justify-between items-center bg-white hover:bg-blue-100 transition rounded-lg px-4 py-2 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-blue-500" />
                      <span
                        className="text-blue-800 font-medium"
                        dangerouslySetInnerHTML={{
                          __html: s.reading.reading_title || "Reading Section",
                        }}
                      />
                    </div>
                    <span className="text-blue-800 font-semibold">
                      {s.reading.reading_duration || 0} min
                    </span>
                  </div>
                )}

                {/* Writing */}
                {s.writing && (
                  <div className="flex justify-between items-center bg-white hover:bg-green-100 transition rounded-lg px-4 py-2 shadow-sm">
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} className="text-green-500" />
                      <span className="text-green-800 font-medium">
                        {s.writing.writing_title || "Writing Section"}
                      </span>
                    </div>
                    <span className="text-green-800 font-semibold">
                      {s.writing.estimated_time_minutes || 0} min
                    </span>
                  </div>
                )}
              </div>

            </div>
          ))}
        </div>

        {/* ✅ Timing & Scoring */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-6 border-yellow-400 rounded-xl p-6 shadow-lg">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
              <Timer className="w-5 h-5 text-yellow-600" />
            </div>
            <h3 className="text-xl font-bold text-yellow-800">⏰ Timing & Scoring</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-bold text-gray-800 mb-2">Duration:</h4>
              <div className="flex items-center bg-white rounded-lg px-3 py-2 mb-2 shadow-sm">
                <Clock className="w-4 h-4 text-yellow-600 mr-2" />
                <span>
                  Total time: <strong>{totalDuration}</strong> minutes
                </span>
              </div>
              <div className="flex items-center bg-white rounded-lg px-3 py-2 shadow-sm">
                <FileText className="w-4 h-4 text-yellow-600 mr-2" />
                <span>Auto-save enabled</span>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-gray-800 mb-2">Scoring:</h4>
              <div className="flex items-center bg-white rounded-lg px-3 py-2 mb-2 shadow-sm">
                <Award className="w-4 h-4 text-yellow-600 mr-2" />
                <span>
                  Reading: 1st try = <strong>1 point</strong>, 2nd try ={" "}
                  <strong>0.5 points</strong>
                </span>
              </div>
              <div className="flex items-center bg-white rounded-lg px-3 py-2 shadow-sm">
                <Award className="w-4 h-4 text-yellow-600 mr-2" />
                <span>
                  Writing: The rubric is provided next to the writing task
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Footer */}
      <div className="bg-white border-t shadow-md flex-shrink-0">
        <div className="max-w-6xl mx-auto px-6 py-4 text-center">
          {viewMode ? (
            <button
              onClick={() => handleStartTest()}
              className="px-8 py-2.5 rounded-lg font-bold text-base bg-gray-500 text-white hover:bg-gray-600 transform hover:scale-105 shadow-lg transition-all duration-300"
            >
              👀 View Only
            </button>
          ) : (
            <button
              onClick={() => handleStartTest()}
              className="px-8 py-2.5 rounded-lg font-bold text-base bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 shadow-lg transition-all duration-300"
            >
              🚀 Start Test
            </button>
          )}

          <p className="mt-2 text-gray-600 text-sm">
            {viewMode ? "You can only view your completed test results." : "Good luck with your test! 💪"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default InstructionsScreen;
